/* Copyright (c) 2020-present CloudIO Inc */

import { useLatest } from '@cloudio-saas/statex';
import hotkeys, { HotkeysEvent } from 'hotkeys-js';
import React, { useEffect } from 'react';
import { useDeveloperMode } from '../../designer/atoms';
import { isEmpty } from '../../utils/isEmpty';

hotkeys.filter = () => true;
const KEY_PREFIX = ['command+shift', 'ctrl+shift'];

export default function useHotkey({
  hotKey,
  text,
  handleHotKey,
  appUid,
  pageId,
  enableInDevMode,
  keysPrefix = KEY_PREFIX,
  filter,
}: {
  text?: string | null;
  hotKey: string;
  handleHotKey: (e: React.MouseEvent<HTMLButtonElement>) => void;
  appUid: string;
  pageId: string;
  enableInDevMode?: boolean;
  keysPrefix?: string[];
  filter?: (e: KeyboardEvent, hotkeysEvent: HotkeysEvent) => boolean;
}): React.ReactNode {
  const devMode = useDeveloperMode(appUid, pageId);
  const latest = useLatest({ handleHotKey, keysPrefix, filter });
  useEffect(() => {
    if ((!enableInDevMode && devMode) || isEmpty(hotKey)) {
      return;
    }
    const keys = latest.current.keysPrefix
      .map((v) => `${v}+${hotKey.toLocaleLowerCase()}`)
      .join(',');
    function keyHandler(e: KeyboardEvent, hotkeysEvent: HotkeysEvent) {
      if (latest.current.filter && !latest.current.filter(e, hotkeysEvent)) {
        return;
      }
      // prevent default browser behavior only if filter matches
      // this is to allow browser to handle hotkeys like ctrl+z
      // we should prevent to allow text fields to use ctrl+shift+H (home page) & ctrl+shift+U (Audit)
      // instead of using browser's default behavior
      e.preventDefault();
      e.stopPropagation();
      latest.current.handleHotKey(
        e as unknown as React.MouseEvent<HTMLButtonElement>,
      );
    }
    hotkeys(keys, {}, keyHandler);
    return () => hotkeys.unbind(keys, keyHandler);
  }, [devMode, enableInDevMode, latest, hotKey]);

  let label: React.ReactNode = text;
  if (!isEmpty(hotKey) && text) {
    const index = text.indexOf(hotKey);
    if (index !== -1) {
      label = (
        <>
          {text.substring(0, index)}
          <u>{hotKey}</u>
          {text.substring(index + 1)}
        </>
      );
    } else {
      label = (
        <>
          {text} (<u>{hotKey}</u>)
        </>
      );
    }
  }
  return label;
}

export function useCmdKey({
  hotKey,
  handler,
  filter,
}: {
  hotKey: string;
  handler: (e: React.MouseEvent<HTMLButtonElement>) => void;
  filter?: (e: KeyboardEvent, hotkeysEvent: HotkeysEvent) => boolean;
}): void {
  const latest = useLatest({ handler, filter });
  useEffect(() => {
    const keys = ['command', 'ctrl']
      .map((v) => `${v}+${hotKey.toLocaleLowerCase()}`)
      .join(',');
    function keyHandler(e: KeyboardEvent, hotkeysEvent: HotkeysEvent) {
      e.preventDefault();
      e.stopPropagation();
      if (latest.current.filter && !latest.current.filter(e, hotkeysEvent)) {
        return;
      }
      latest.current.handler(
        e as unknown as React.MouseEvent<HTMLButtonElement>,
      );
    }
    hotkeys(keys, {}, keyHandler);
    return () => hotkeys.unbind(keys, keyHandler);
  }, [latest, hotKey]);
}
